// The app's audio: starts and stops the voice through the audio bridge
// (docs/contracts/audio-bridge.md), owns the output gain, and tells the controls
// and panels when the voice comes and goes. Browsers allow audio only after a user
// gesture, so start() must be called from a click or key handler.
//
// The voice is the wasm host (#28) running the source module, with the engine's
// bundle and the impulse responses of its listener positions (#41); the mock
// processor can stand in for it (options.processor).

import {
  AudioEngine,
  VOICE_PROCESSOR,
  loadEngineBundle,
  loadVoiceWasm,
  type ProcessorModule,
} from "../../audio/engine";
import { listenerIndex, listenerTable, loadListenerAssets, type ListenerTable } from "../../audio/listeners";
import type { ErrorMessage, ReadyMessage, TelemetryRecord, TimelineEvent } from "../../audio/protocol";

export type AudioStatus = "off" | "starting" | "on" | "failed";

export interface AudioSessionOptions {
  processor?: ProcessorModule;
  /** The registry id of the source module the voice runs. */
  source?: string;
  /** The engine data folder whose bundle the voice gets (default vw-ct). */
  engine?: string;
}

type Listener = (status: AudioStatus) => void;

/** How fast a volume change settles, in seconds (time constant of setTargetAtTime). */
const GAIN_SMOOTHING_S = 0.02;

export class AudioSession {
  private engineRef: AudioEngine | null = null;
  private gain: GainNode | null = null;
  private statusValue: AudioStatus = "off";
  private errorText = "";
  private processorName = "";
  private level = 1;
  private readonly listeners = new Set<Listener>();
  private readonly errorListeners = new Set<(e: ErrorMessage) => void>();
  private readonly options: AudioSessionOptions;
  private pending: Promise<void> | null = null;

  /**
   * The load parameters the voice starts with (load.params of init), asked for at
   * every start; the powertrain driver sets it so a voice starts at the right speed.
   */
  startParams: () => Record<string, number> = () => ({ rpm: 0 });

  /**
   * The listener position the voice starts at, asked for at every start: an id
   * of listeners, or null for the engine's default. The listener panel sets it.
   */
  startListener: () => string | null = () => null;

  /**
   * The tier the voice starts at, asked for at every start: an index of the
   * source module's quality table, or null for the module's default (its first
   * live tier). The quality panel (#32) sets it.
   */
  startQuality: () => number | null = () => null;

  constructor(options: AudioSessionOptions = {}) {
    this.options = options;
  }

  get status(): AudioStatus {
    return this.statusValue;
  }

  /** The running engine, or null while the audio is off. */
  get engine(): AudioEngine | null {
    return this.statusValue === "on" ? this.engineRef : null;
  }

  /** The voice's ready message while it runs. */
  get ready(): ReadyMessage | null {
    return this.engine?.ready ?? null;
  }

  /** The newest telemetry record while the audio runs. */
  get latest(): TelemetryRecord | null {
    return this.engine?.latest ?? null;
  }

  /** The registry id of the source module the voice runs (its quality table is data/quality/<id>.toml). */
  get source(): string {
    return this.options.source ?? "pulse-train";
  }

  /** The engine data folder the voice runs. */
  get engineId(): string {
    return this.options.engine ?? "vw-ct";
  }

  /** The engine's listener positions, or null if it has none (it is heard direct). */
  get listenerTable(): ListenerTable | null {
    return listenerTable(this.engineId);
  }

  /** The processor's name from its hello ("mock" or "voice"), or "" before the first start. */
  get processor(): string {
    return this.processorName;
  }

  /** Why the last start failed. */
  get error(): string {
    return this.errorText;
  }

  /** Starts the voice. Call it from a user gesture. Resolves once the voice is ready (or has failed). */
  start(): Promise<void> {
    if (this.statusValue === "on") return Promise.resolve();
    if (this.pending !== null) return this.pending;
    this.setStatus("starting");
    this.pending = this.open().finally(() => (this.pending = null));
    return this.pending;
  }

  /** Stops the voice and closes its audio context. */
  async stop(): Promise<void> {
    await this.pending;
    const engine = this.engineRef;
    this.engineRef = null;
    this.gain = null;
    if (engine !== null) await engine.close();
    this.setStatus("off");
  }

  /** Output level, 0 to 1; muting is a level of 0, so the voice keeps running and in sync. */
  setLevel(level: number): void {
    this.level = Math.min(1, Math.max(0, level));
    const gain = this.gain;
    if (gain !== null) gain.gain.setTargetAtTime(this.level, gain.context.currentTime, GAIN_SMOOTHING_S);
  }

  /** Sends a control to the voice; does nothing while the audio is off. */
  control(id: string, value: number): void {
    this.engine?.control(id, value);
  }

  /** Whether the running voice accepts a control. */
  accepts(id: string): boolean {
    return this.ready?.controls.includes(id) ?? false;
  }

  onStatus(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onError(listener: (e: ErrorMessage) => void): () => void {
    this.errorListeners.add(listener);
    return () => this.errorListeners.delete(listener);
  }

  private async open(): Promise<void> {
    let engine: AudioEngine | null = null;
    try {
      const processor = this.options.processor ?? VOICE_PROCESSOR;
      const voice = processor === VOICE_PROCESSOR;
      // Fetched before the context exists, so a missing file fails before any audio starts.
      const [wasm, bundle, assets] = voice
        ? await Promise.all([loadVoiceWasm(), loadEngineBundle(this.engineId), loadListenerAssets(this.engineId)])
        : [undefined, "{}", []];
      engine = await AudioEngine.create({ processor, connect: false });
      this.processorName = (await engine.hello).processor;
      const gain = new GainNode(engine.context, { gain: this.level });
      engine.node.connect(gain).connect(engine.context.destination);
      engine.onError((e) => {
        for (const l of [...this.errorListeners]) l(e);
      });
      await engine.resume();
      // The voice starts at the chosen listener with no crossfade: a change at frame 0.
      const listener = voice ? listenerIndex(this.listenerTable, this.startListener()) : 0;
      const timeline: TimelineEvent[] = [];
      if (listener !== 0) timeline.push({ frame: 0, control: "listener", value: listener });
      // A tier other than the default starts with a switch at frame 0, heard as a 20 ms fade-in.
      const quality = this.startQuality();
      if (quality !== null) timeline.push({ frame: 0, control: "quality", value: quality });
      await engine.start({
        bundle,
        powertrain: { source: this.source, load: { kind: "free", params: this.startParams() } },
        ...(wasm === undefined ? {} : { wasm }),
        ...(assets.length === 0 ? {} : { assets }),
        ...(timeline.length === 0 ? {} : { timeline }),
      });
      this.engineRef = engine;
      this.gain = gain;
      this.errorText = "";
      this.setStatus("on");
    } catch (error) {
      this.errorText = error instanceof Error ? error.message : String(error);
      console.error("audio: the voice did not start", error);
      await engine?.close().catch(() => {});
      this.setStatus("failed");
    }
  }

  private setStatus(status: AudioStatus): void {
    if (status === this.statusValue) return;
    this.statusValue = status;
    for (const l of [...this.listeners]) l(status);
  }
}
